/* @flow */

import * as React from 'react'
import { Modal } from 'react-bootstrap'
import groupBy from 'lodash/groupBy'
import uuid from 'uuid'

import WebshopActionButton from './WebshopActionButton'
import { useLinesReducer } from '../../products/components/ProductTable2'

import WebshopProductTable from './WebshopProductTable'
import { defaultLinePriceExtractor } from '../../products/components/ProductTable2/configuration/columns/unit_price'

import { createCartPersistenceKey } from '../shared'
import { SessionContext } from '../../shared'

const EmployeeDiscountModalButton = ({
  lines,
  onLineChange: propsOnLineChange,
  onSaveEmployeeDiscountLines,
  prices,
  products,
  render,
  ...props
}) => {
  const { entity } = React.useContext(SessionContext)

  const [updatedLines, onLineChange] = useLinesReducer([])
  const [showModal, setShowModal] = React.useState(false)

  React.useEffect(() => {
    const linesByKey = groupBy(lines, line => createCartPersistenceKey(line))

    const mappedLines = []
    for (const linesOfKey of Object.values(linesByKey)) {
      let line = linesOfKey.find(line => line.employee_discount === true)

      if (!line) {
        line = { ...linesOfKey[0], id: uuid(), __new: true, quantity: 0 }
      }

      const productPrices = prices.find(p => p.product_id === line.product_id)

      if (!productPrices) {
        continue
      }

      let priceIndex = productPrices.employee_discount_variants[line.variant_id]

      if (priceIndex === undefined || priceIndex === false) {
        priceIndex = productPrices.variants[line.variant_id]
      }

      if (priceIndex === undefined || priceIndex === false) {
        continue
      }

      const price = productPrices.prices[priceIndex]

      const copy = {
        ...line,
        ...defaultLinePriceExtractor(price, false),
        employee_discount: true,
      }

      mappedLines.push(copy)
    }

    onLineChange({
      type: 'set',
      lines: mappedLines,
    })
  }, [lines, onLineChange, prices])

  const hasEmployeeDiscountPrices = React.useMemo(() => {
    const productIds = products.map(product => product.id)

    return (
      prices.filter(price => {
        return (
          productIds.includes(price.product_id) &&
          Object.keys(price.employee_discount_variants).length > 0
        )
      }).length > 0
    )
  }, [prices, products])

  const onSubmit = React.useCallback(
    (...args) => {
      const promise = onSaveEmployeeDiscountLines(...args)
      if (promise) {
        return promise.then(response => {
          if (!response.error) {
            setShowModal(false)
          }
        })
      }
    },
    [onSaveEmployeeDiscountLines, setShowModal]
  )

  if (!hasEmployeeDiscountPrices || entity.entity_type === 'shop') {
    return null
  }

  return (
    <>
      {render({ onClick: () => setShowModal(true) })}

      {showModal && (
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          dialogClassName="xl-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Employee discounts</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <WebshopProductTable
              employeeDiscountTable
              lines={updatedLines}
              onLineChange={onLineChange}
              prices={prices}
              products={products}
              {...props}
            />
          </Modal.Body>
          <Modal.Footer>
            <WebshopActionButton
              size="large"
              color="white"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </WebshopActionButton>
            <WebshopActionButton
              color="primary"
              size="large"
              iconClass="glyphicon glyphicon-ok"
              onClick={() => onSubmit(updatedLines)}
            >
              Save
            </WebshopActionButton>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}

export default EmployeeDiscountModalButton
